import type { AssetDefinition } from '@cesdk/cesdk-js';
import {
	ImageSize,
	WebRoutes,
	cropTransparentEdges,
	ensureUnder100MP,
	getBlobUrl,
	getImageIdAndExtension,
	getSizedImageSourceSet,
	sizedImage,
	withDomain,
} from '@theprintbar/shared';
import axios from 'axios';
//@ts-ignore
import { changeDpiBlob } from 'changedpi';
import { openDB } from 'idb';

export const localAssetType = 'localUpload';
export const storageKey = 'TPBUploads';
// --- DB helper ---
async function getDB() {
	try {
		return openDB(storageKey, 1, {
			upgrade(db) {
				if (!db.objectStoreNames.contains(storageKey)) {
					db.createObjectStore(storageKey);
				}
			},
		});
	} catch (e) {
		console.error('Failed to open IndexedDB', e);
		return null;
	}
}

export async function storeLocalImage(asset: AssetDefinition) {
	const db = await getDB();
	if (!db) {
		//TODO
		throw 'Browser is not supported, please upgrade your browser to use this feature.';
	} else {
		await db.put(storageKey, asset, asset.id);
	}
}

export async function getLocalImages(indexKey?: string): Promise<AssetDefinition[]> {
	const db = await getDB();
	if (!db) {
		return [];
	} else {
		return (await db.getAll(storageKey, indexKey)) || [];
	}
}

export async function removeUploadFromStorage(id: string) {
	try {
		const db = await getDB();
		if (!db) {
			console.log('couldnt find db');
		} else {
			await db.delete(storageKey, id);
		}
	} catch (e) {
		console.log('Failed to remove upload from storage');
	}
}

export async function isLocalUpload(assetId: string) {
	const uploads = await getLocalImages(assetId);
	return !!uploads.length;
}

export async function getUploadsFromStorage(): Promise<AssetDefinition[]> {
	try {
		return (await getLocalImages()).map(a => {
			delete (a as any).file;
			return a as AssetDefinition;
		});
	} catch (e) {
		console.log('Failed to get uploads from storage');
		return [];
	}
}

export async function handleLocalUpload(webDomain: string, onProgress: (progress: number) => void, file: File, sourceId?: string) {
	try {
		if (sourceId) {
			const mimeTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp', 'image/bmp'];

			if (!mimeTypes.includes(file.type)) {
				throw 'File not supported. Please upload a JPEG, PNG, SVG, WEBP or BMP image.';
			}
		}

		//Make sure to crop transparent edges and convert to png if the file is an image
		let newFile = file;
		let width = 0;
		let height = 0;

		const { id, extension } = getImageIdAndExtension(file.name);
		let newName = `${id}.${extension}`;

		try {
			if (file.type.includes('image')) {
				if (file.type === 'image/svg+xml') {
					const svgText = await file.text(); // Read the SVG as text
					const svgDoc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
					const svgElement = svgDoc.querySelector('svg');
					if (!svgElement) throw '';

					// Get width and height from the SVG attributes
					width = parseInt(svgElement.getAttribute('width') || svgElement.viewBox.baseVal.width.toString());
					height = parseInt(svgElement.getAttribute('height') || svgElement.viewBox.baseVal.height.toString());
				} else {
					const data = await cropTransparentEdges(file);
					const ensuredSizeData = await ensureUnder100MP(data.blob);
					const dpiBlob = await changeDpiBlob(ensuredSizeData.blob, 300);
					newName = `${id}.png`; // Change the extension to PNG after cropping
					newFile = new File([dpiBlob], newName, {
						type: 'image/png',
						lastModified: file.lastModified,
					});
					width = ensuredSizeData.width;
					height = ensuredSizeData.height;
				}
			}
		} catch (e) {
			console.error('Failed to crop transparent edges', e);
		}

		const { url, key } = await uploadDesignToRemoteStorage({ webDomain, file: newFile, onProgress });
		const assetDefinition: AssetDefinition = {
			id: key, // A unique ID identifying the uploaded image
			label: { en: id }, // A label to display in the UI
			meta: {
				insertedAt: Date.now(),
				type: localAssetType,
				fileType: newFile.type,
				fillType: '//ly.img.ubq/fill/image',
				uploaded: new Date().toISOString(), // The date and time the image was uploaded
				uri: url, // A fully qualified URL pointing at the source of the image
				thumbUri: sizedImage(url, ImageSize.Medium), // A fully qualified URL pointing at a version of source of the image, that's suitable for thumbnails
				width,
				height,
				fileSize: newFile.size,
			},
			payload: width
				? {
						sourceSet: getSizedImageSourceSet({ url, originalHeight: height, originalWidth: width }),
				  }
				: undefined,
		};
		await storeLocalImage(assetDefinition);
		//Add asset definition to the
		return assetDefinition as AssetDefinition;
	} catch (e) {
		console.log('whoops');
		console.error(e);
		throw e;
	}
}

interface DesignerUploadProps {
	webDomain: string;
	onProgress?: (progress: number) => void;
	file: File;
}
export async function uploadDesignToRemoteStorage({ webDomain, onProgress, file }: DesignerUploadProps) {
	const { id, extension } = getImageIdAndExtension(file.name);
	const newName = `${id}.${extension}`;
	const { putUrl, key } = await (
		await fetch(withDomain(WebRoutes.API.images.getSignedUpload, webDomain), {
			method: 'POST',
			body: newName,
		})
	).json();

	const res = await axios.put(putUrl, file, {
		onUploadProgress: progressEvent => {
			onProgress?.(progressEvent.progress || 0);
		},
		headers: {
			'Content-Type': file.type,
			'Cross-Origin-Embedder-Policy': 'unsafe-none',
			'Cross-Origin-Opener-Policy': 'same-origin-allow-popups',
		},
	});

	if (res.status !== 200) {
		throw new Error('Failed to upload image');
	}

	return {
		key,
		url: withDomain(WebRoutes.API.images.viewUpload(key), webDomain),
	};
}
